// Single source of truth for analytics event names.
//
// Convention: "Object Action", past tense for completed actions (e.g.
// "Stream Created"). Track only from the SUCCESS path (after the API/service
// call resolves). Pass business-relevant properties — NEVER PII/secrets
// (no stream keys, tokens, passwords, signed URLs, card data, phone numbers).
//
// Stream lifecycle is intentionally UNIFIED: one "Stream Created"/"Stream
// Enabled"/… event carries a `streamType` property (live | restream |
// subsecond | scheduled | tvplayout | transcoder | vod) instead of one event
// per product — this keeps funnels/segmentation clean. Same idea for copy
// events (one "Embed Code Copied" / "Content URL Copied" with `contentType`).
export const EVENTS = {
	// ---------------------------------------------------------------- Auth
	SIGNUP_CODE_REQUESTED: 'Signup Code Requested',
	SIGNUP_VERIFIED: 'Signup Verified',
	LOGIN_CODE_REQUESTED: 'Login Code Requested',
	LOGIN_CODE_VERIFIED: 'Login Code Verified',
	// Opt-in password sign-in (accounts with passwordLoginEnabled).
	LOGIN_PASSWORD_VERIFIED: 'Login Password Verified',
	LOGIN_PASSWORD_CODE_FALLBACK: 'Login Password Code Fallback',
	LOGIN_COMPLETED: 'Login Completed',
	LOGIN_SESSION_RESTORED: 'Login Session Restored',
	GOOGLE_AUTH_STARTED: 'Google Auth Started',
	// Required phone step after a Google signup; completion = Added / Shown.
	POST_AUTH_PHONE_SHOWN: 'Post-Auth Phone Shown',
	POST_AUTH_PHONE_ADDED: 'Post-Auth Phone Added',
	ADMIN_LOGIN_COMPLETED: 'Admin Login Completed',
	TWO_FACTOR_VERIFIED: 'Two-Factor Verified',
	TWO_FACTOR_ENABLED: 'Two-Factor Enabled',
	TWO_FACTOR_DISABLED: 'Two-Factor Disabled',
	PASSWORD_RESET_REQUESTED: 'Password Reset Requested',
	PASSWORD_RESET_COMPLETED: 'Password Reset Completed',
	LOGGED_OUT: 'Logged Out',

	// ------------------------------------------------------- Account & team
	PROFILE_UPDATED: 'Profile Updated',
	ACCOUNT_PASSWORD_CHANGED: 'Account Password Changed',
	PASSWORD_LOGIN_ENABLED: 'Password Login Enabled',
	PASSWORD_LOGIN_DISABLED: 'Password Login Disabled',
	ALL_SESSIONS_REVOKED: 'All Sessions Revoked',
	PHONE_NUMBER_ADDED: 'Phone Number Added',
	PHONE_NUMBER_REMOVED: 'Phone Number Removed',
	TEAM_CREATED: 'Team Created',
	TEAM_RENAMED: 'Team Renamed',
	TEAM_MEMBER_INVITED: 'Team Member Invited',
	TEAM_MEMBER_REMOVED: 'Team Member Removed',
	TEAM_JOINED: 'Team Joined',
	// Projects partition an account's resources; the API calls them workspaces.
	PROJECT_CREATED: 'Project Created',
	PROJECT_SWITCHED: 'Project Switched',
	PROJECT_UPDATED: 'Project Updated',
	PROJECT_DELETED: 'Project Deleted',
	API_TOKEN_CREATED: 'API Token Created',
	API_TOKEN_REVOKED: 'API Token Revoked',
	VMIX_TOKEN_CREATED: 'vMix Token Created',
	VMIX_TOKEN_DELETED: 'vMix Token Deleted',

	// -------------------------------------------- Stream lifecycle (unified)
	// Properties: streamType, streamId, regionName, etc.
	STREAM_CREATED: 'Stream Created',
	STREAM_ENABLED: 'Stream Enabled',
	STREAM_DISABLED: 'Stream Disabled',
	DESTINATION_ADDED: 'Destination Added',
	DESTINATION_REMOVED: 'Destination Removed',
	DESTINATION_TOGGLED: 'Destination Toggled',
	STREAM_SOURCE_LINKED: 'Stream Source Linked',

	// ------------------------------------------------ Stream configuration
	RECORDING_TOGGLED: 'Recording Toggled',
	BACKUP_CONFIGURED: 'Backup Configured',
	ABR_TOGGLED: 'Adaptive Bitrate Toggled',
	ABR_SETTING_SAVED: 'Adaptive Bitrate Setting Saved',
	DRM_TOGGLED: 'DRM Toggled',
	SECURITY_FEATURE_TOGGLED: 'Security Feature Toggled',
	CHAT_TOGGLED: 'Chat Toggled',
	WEBHOOK_ADDED: 'Webhook Added',
	STREAM_ALERT_CONFIGURED: 'Stream Alert Configured',
	STREAM_ALERT_DISABLED: 'Stream Alert Disabled',

	// --------------------------------------------------- Multiview / others
	MULTIVIEW_CREATED: 'Multiview Created',
	MULTIVIEW_CONFIGURED: 'Multiview Configured',
	MULTIVIEW_TOGGLED: 'Multiview Toggled',
	TRANSCODER_CREATED: 'Transcoder Created',
	TRANSCODER_PROFILE_CONFIGURED: 'Transcoder Profile Configured',
	TRANSCODER_DESTINATION_CONFIGURED: 'Transcoder Destination Configured',
	TV_PLAYOUT_CREATED: 'TV Playout Created',
	TV_PLAYOUT_SCHEDULE_CONFIGURED: 'TV Playout Schedule Configured',
	SCHEDULED_STREAM_SCHEDULE_CONFIGURED: 'Scheduled Stream Schedule Configured',

	// ----------------------------------------------------------------- VOD
	VOD_UPLOAD_STARTED: 'VOD Upload Started',
	VOD_UPLOADED: 'VOD Uploaded',
	VOD_FOLDER_CREATED: 'VOD Folder Created',
	VOD_FOLDER_DELETED: 'VOD Folder Deleted',
	VIDEO_DELETED: 'Video Deleted',
	VIDEO_SCHEDULE_SAVED: 'Video Schedule Saved',
	FOLDER_OTT_TOGGLED: 'Folder OTT Toggled',
	CLIP_CREATED: 'Clip Created',
	CAPTIONS_ADDED: 'Captions Added',
	CHAPTERS_ADDED: 'Chapters Added',
	LIVE_TO_VOD_EXPORT_STARTED: 'Live-to-VOD Export Started',
	LIVE_TO_VOD_CREATED: 'Live-to-VOD Created',
	VOD_ADDED_TO_STREAM: 'VOD Added To Stream',

	// ---------------------------------------------------------- Highlights
	HIGHLIGHT_CREATED: 'Highlight Created',
	HIGHLIGHT_DELETED: 'Highlight Deleted',
	// Live captioning flows. Properties: langs (codes), durationSec. Never stream keys.
	CAPTIONING_CREATED: 'Captioning Created',
	CAPTIONING_UPDATED: 'Captioning Updated',
	CAPTIONING_DELETED: 'Captioning Deleted',
	CAPTIONING_STARTED: 'Captioning Started',
	CAPTIONING_EXTENDED: 'Captioning Extended',
	// A plan limit got in the way: any 409/403/429 limit error from core, or a
	// control disabled by a limit being hovered/clicked. Properties: code
	// (captions_pool_exhausted | captions_concurrency_limit | ...), action
	// (start|extend|save|create|hover|click), surface (dashboard|manage).
	CAPTIONING_CAP_HIT: 'Captioning Cap Hit',
	// Properties: maxLangs, attempted (the count the user was going for).
	CAPTIONING_LANGUAGE_BLOCKED: 'Captioning Language Blocked',
	// Recognition settings saved. Properties: flowId, vocabCount, replacementsCount,
	// removeDisfluencies, profanity, speakers, outputLocale. Never the terms themselves.
	CAPTIONING_DICTIONARY_SAVED: 'Captioning Dictionary Saved',
	// Properties: added, duplicates, errors (line counts from the import preview), mode (merge|replace).
	CAPTIONING_DICTIONARY_IMPORTED: 'Captioning Dictionary Imported',
	// Captions restarted to apply saved settings. Properties: flowId.
	CAPTIONING_SETTINGS_APPLIED_NOW: 'Captioning Settings Applied Now',
	// Settings copied from another flow into the editor. Properties: flowId, sourceFlowId, mode (merge|replace), vocabCount.
	CAPTIONING_DICTIONARY_COPIED: 'Captioning Dictionary Copied',
	// A caption file was saved from the manage page. Properties: flowId, lang, format (vtt|srt|json), running.
	CAPTIONING_EXPORT_DOWNLOADED: 'Captioning Export Downloaded',
	// The "Translate with Gemini" switch was flipped. Properties: flowId, enabled.
	CAPTIONING_GEMINI_TOGGLED: 'Captioning Gemini Toggled',
	// The account's own SyncWords keys were connected / disconnected (Settings -> Developers).
	CAPTIONING_PROVIDER_CONNECTED: 'Captioning Provider Connected',
	CAPTIONING_PROVIDER_DISCONNECTED: 'Captioning Provider Disconnected',
	// Properties: highlightId, streamId, durationSec, source (quick30|quick60|quick300|manual).
	// streamId (not the stream key) per the no-secrets rule above.
	HIGHLIGHT_CLIP_DOWNLOADED: 'Highlight Clip Downloaded',
	// Properties: highlightId, streamId.
	HIGHLIGHT_SCREENSHOT: 'Highlight Screenshot Captured',

	// -------------------------------------------------- Sharing / embedding
	// Properties: contentType (live|vod|clip|dynamic|playlist|paywall), copyType.
	EMBED_CODE_COPIED: 'Embed Code Copied',
	CONTENT_URL_COPIED: 'Content URL Copied',

	// ------------------------------------------------------- Money moments
	PLANS_CATALOG_VIEWED: 'Plans Catalog Viewed',
	PLAN_SELECTED: 'Plan Selected',
	CHECKOUT_STARTED: 'Checkout Started',
	CHECKOUT_COMPLETED: 'Checkout Completed', // server/webhook-side (Fastspring)
	TRIAL_STARTED: 'Trial Started',
	SUBSCRIPTION_CANCELLED: 'Subscription Cancelled',
	UPGRADE_CLICKED: 'Upgrade Clicked',

	// ------------------------------------------------- Monetization (creator)
	PAYWALL_CREATED: 'Paywall Created',
	PAYWALL_PRICE_UPDATED: 'Paywall Price Updated',
	PAYWALL_SOURCE_ASSIGNED: 'Paywall Source Assigned',
	PAYWALL_ACCESS_GRANTED: 'Paywall Access Granted',

	// ----------------------------------------------- Players / playlists / etc
	DYNAMIC_PLAYER_CREATED: 'Dynamic Player Created',
	DYNAMIC_PLAYER_SOURCE_ASSIGNED: 'Dynamic Player Source Assigned',
	PLAYLIST_CREATED: 'Playlist Created',
	PLAYLIST_CONTENT_ADDED: 'Playlist Content Added',
	SHOWCASE_CREATED: 'Showcase Created',

	// ---------------------------------------------------------- Engagement
	// Properties: range (preset key), scope (all|live|vod), items (selected count).
	ANALYTICS_VIEWED: 'Analytics Dashboard Viewed',
	// Properties: range, items, sections (kpis|trend|leaderboard), metric.
	ANALYTICS_EXPORTED: 'Analytics Exported',
	// Properties: contentType (live|vod), source (leaderboard|deep_link).
	// Content ids are stream keys, so they are deliberately not sent.
	ANALYTICS_CONTENT_OPENED: 'Analytics Content Opened',
}
